import { Business, getStaticCategory } from '@/api/models';
import { AdvancedTable } from '@/components/ui/AdvancedTable';
import { Button } from '@/components/ui/Button';
import { CardBox } from '@/components/ui/CardBox';
import { mdiDelete, mdiEye, mdiPencil } from '@mdi/js';
import * as React from 'react';

export interface BusinessesTableProps {
  businesses: Business[];
  isLoading: boolean;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (key: string) => void;
  onRowClick: (business: Business) => void;
  onEditBusiness: (business: Business) => void;
  onDeleteBusiness: (business: Business) => void;
  isDeleting: boolean;
  selectedCountry?: string;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Active':
      return 'bg-green-100 text-green-700';
    case 'Pending':
      return 'bg-yellow-100 text-yellow-700';
    case 'Inactive':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const getScoreColor = (score: number) => {
  if (score >= 70) return 'text-green-600';
  if (score >= 40) return 'text-yellow-600';
  return 'text-red-600';
};

const getScoreBgColor = (score: number) => {
  if (score >= 70) return 'bg-green-100';
  if (score >= 40) return 'bg-yellow-100';
  return 'bg-red-100';
};

export const BusinessesTable: React.FC<BusinessesTableProps> = ({
  businesses,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
  onEditBusiness,
  onDeleteBusiness,
  isDeleting,
  selectedCountry,
}) => {
  const columns = React.useMemo(() => {
    const baseColumns = [
      { key: 'image', label: '', sortable: false, className: 'w-14' },
      { key: 'name', label: 'Name', sortable: true },
      { key: 'city', label: 'City', sortable: true, className: 'w-32' },
      ...(!selectedCountry
        ? [
            {
              key: 'country',
              label: 'Country',
              sortable: true,
              className: 'w-32',
            },
          ]
        : []),
      {
        key: 'categories',
        label: 'Categories',
        sortable: false,
        className: 'w-48',
      },
      { key: 'status', label: 'Status', sortable: true, className: 'w-28' },
      { key: 'score', label: 'Score', sortable: false, className: 'w-24' },
      { key: 'reviews', label: 'Reviews', sortable: false, className: 'w-24' },
      { key: 'createdAt', label: 'Created', sortable: true, className: 'w-32' },
    ];
    return baseColumns;
  }, [selectedCountry]);

  const renderCell = (
    key: string,
    _value: Business[keyof Business],
    item: Business,
  ): React.ReactNode => {
    switch (key) {
      case 'image': {
        const primaryImage = item.images?.find(img => img.primary);
        const imageUrl =
          primaryImage?.image?.primaryUrl ||
          item.images?.[0]?.image?.primaryUrl;
        return (
          <div className='w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center'>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={item.name}
                className='w-full h-full object-cover'
              />
            ) : (
              <span className='text-gray-400 text-xs'>No img</span>
            )}
          </div>
        );
      }
      case 'name':
        return (
          <div>
            <div className='font-medium text-gray-900'>{item.name}</div>
            <div className='text-sm text-gray-500 truncate max-w-xs'>
              {item.address}
            </div>
          </div>
        );
      case 'city':
        return <div className='text-sm text-gray-900'>{item.city || '-'}</div>;
      case 'country':
        return (
          <div className='text-sm text-gray-900'>{item.country || '-'}</div>
        );
      case 'categories':
        return (
          <div className='flex flex-wrap gap-1'>
            {item.businessCategories && item.businessCategories.length > 0 ? (
              <>
                {item.businessCategories.slice(0, 2).map((bc, index) => {
                  const staticCategory = getStaticCategory(
                    bc.category?.name || '',
                  );
                  return (
                    <span
                      key={index}
                      className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700'
                    >
                      {staticCategory?.emoji}{' '}
                      {staticCategory?.label || bc.category?.name || 'Unknown'}
                    </span>
                  );
                })}
                {item.businessCategories.length > 2 && (
                  <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600'>
                    +{item.businessCategories.length - 2}
                  </span>
                )}
              </>
            ) : (
              <span className='text-gray-400 text-xs'>No categories</span>
            )}
          </div>
        );
      case 'status':
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status || '')}`}
          >
            {item.status}
          </span>
        );
      case 'score': {
        const score = (item as Business & { workFriendlyScore?: number })
          .workFriendlyScore;
        const reviewCount = (
          item as Business & { approvedReviewCount?: number }
        ).approvedReviewCount;
        const hasScore = score !== undefined && score !== null;
        const hasReviews = reviewCount !== undefined && reviewCount > 0;

        if (!hasScore) {
          return <span className='text-gray-400 text-xs'>-</span>;
        }

        return (
          <div className='flex items-center gap-1'>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getScoreBgColor(score)} ${getScoreColor(score)}`}
            >
              {Math.round(score)}
            </span>
            {!hasReviews && (
              <span
                className='w-1.5 h-1.5 rounded-full bg-gray-300'
                title='No reviews yet'
              />
            )}
          </div>
        );
      }
      case 'reviews':
        return (
          <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700'>
            {item.reviews?.length || 0}
          </span>
        );
      case 'createdAt':
        return (
          <div className='text-sm text-gray-900'>
            {item.createdAt
              ? new Date(item.createdAt).toLocaleDateString()
              : 'N/A'}
          </div>
        );
      default:
        return String(_value || '-');
    }
  };

  const renderActions = (business: Business) => (
    <div className='flex items-center space-x-2'>
      <Button
        icon={mdiEye}
        small
        color='info'
        title='View Details'
        onClick={() => onRowClick(business)}
      />
      <Button
        icon={mdiPencil}
        small
        color='warning'
        title='Edit Business'
        onClick={() => onEditBusiness(business)}
      />
      <Button
        icon={mdiDelete}
        small
        color='danger'
        title='Delete Business'
        loading={isDeleting}
        onClick={() => onDeleteBusiness(business)}
      />
    </div>
  );

  return (
    <CardBox hasTable>
      <AdvancedTable
        columns={columns}
        data={businesses}
        loading={isLoading}
        onSort={onSort}
        sortKey={sortBy}
        sortDirection={sortOrder}
        renderCell={renderCell}
        renderActions={renderActions}
        onRowClick={onRowClick}
        emptyMessage='No businesses found. Try adjusting your search criteria.'
      />
    </CardBox>
  );
};
